import React, { createContext, useState, useEffect, useContext } from "react";

interface User {
  id: string;
  email: string;
  name: string;
  isVerified: boolean; // For age verification
  avatarUrl?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  verifyAge: (dateOfBirth: Date) => boolean;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isError: false,
  error: null,
  login: async () => {},
  signup: async () => {},
  logout: () => {},
  verifyAge: () => false,
  resetPassword: async () => {},
  updateProfile: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would validate the token with your backend
        const savedUser = localStorage.getItem("user");

        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }

        setIsError(false);
        setError(null);
      } catch (err) {
        setIsError(true);
        setError("Authentication failed");
        console.error("Auth check failed:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // This would be an API call in a real app
      // const response = await apiClient.post('/auth/login', { email, password });

      // For demo purposes:
      if (!email || !password) {
        throw new Error("Email and password are required");
      }

      // Simulate successful login
      const mockUser: User = {
        id: "user-1",
        email,
        name: email.split("@")[0],
        isVerified: true,
      };

      setUser(mockUser);
      localStorage.setItem("user", JSON.stringify(mockUser));
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err.message : "Login failed");
      console.error("Login error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // This would be an API call in a real app
      // const response = await apiClient.post('/auth/register', { email, password, name });

      if (!email || !password || !name) {
        throw new Error("All fields are required");
      }

      // Simulate successful signup
      const mockUser: User = {
        id: "user-" + Date.now(),
        email,
        name,
        isVerified: false, // New users need to verify their age
      };

      setUser(mockUser);
      localStorage.setItem("user", JSON.stringify(mockUser));
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err.message : "Signup failed");
      console.error("Signup error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  const verifyAge = (dateOfBirth: Date): boolean => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    const isOldEnough = age >= 21; // 21+ for cannabis

    if (isOldEnough && user) {
      const updatedUser = { ...user, isVerified: true };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }

    return isOldEnough;
  };

  const resetPassword = async (email: string) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // This would be an API call in a real app
      // await apiClient.post('/auth/reset-password', { email });

      if (!email) {
        throw new Error("Email is required");
      }

      // Simulate password reset
      console.log(`Password reset email sent to ${email}`);
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err.message : "Password reset failed");
      console.error("Password reset error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      // This would be an API call in a real app
      // await apiClient.put('/users/profile', userData);

      if (!user) {
        throw new Error("You must be logged in to update your profile");
      }

      // Update user data
      const updatedUser = { ...user, ...userData };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    } catch (err) {
      setIsError(true);
      setError(err instanceof Error ? err.message : "Profile update failed");
      console.error("Profile update error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isError,
        error,
        login,
        signup,
        logout,
        verifyAge,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => useContext(AuthContext);

export default AuthContext;
